import { useEffect, useState } from 'react';
import SearchableSelect from '../components/SearchableSelect';
import YearSelect from '../components/YearSelect';
import { getFilterOptions, getBriefReport } from '../api/dashboard';
import { formatCurrency, formatPercent } from '../utils/format';
import { getDefaultDashboardFilters, yearDateRange, detectSelectedYear } from '../utils/filters';
import { useAuth } from '../context/AuthContext';

const GROUP_BY_OPTIONS = [
  { value: '', label: 'None (single total)' },
  { value: 'channel', label: 'Channel' },
  { value: 'subChannel', label: 'Sub-Channel' },
  { value: 'salesmanCode', label: 'Salesman' },
  { value: 'customerCode', label: 'Customer' },
  { value: 'month', label: 'Month' },
];

export default function BriefReport() {
  const { canViewGP } = useAuth();
  const [filters, setFilters] = useState(getDefaultDashboardFilters);
  const [groupBy, setGroupBy] = useState('');
  const [options, setOptions] = useState({ customers: [], salesmen: [], products: [], channels: [], subChannels: [], channelSubChannels: {} });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedYear = detectSelectedYear(filters);

  useEffect(() => {
    // Salesmen are scoped server-side to the selected Year, so a salesman with zero
    // sales that year never shows up as a filter option in the first place.
    getFilterOptions(selectedYear ? { year: selectedYear } : undefined).then((opts) => {
      setOptions(opts);
      setFilters((f) => {
        let next = f;
        // The default view opens on the current calendar year, but if that year has no
        // data at all, fall back to the most recent year that actually has data instead
        // of silently showing an empty report.
        const year = detectSelectedYear(next);
        if (year && opts.years?.length && !opts.years.includes(Number(year))) {
          next = { ...next, ...yearDateRange(opts.years[0]) };
        }
        if (next.salesmanCode && !opts.salesmen.some((s) => s.code === next.salesmanCode)) {
          next = { ...next, salesmanCode: '' };
        }
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    // A Product is the whole point of this report — without one, "group by unit" alone
    // blends every unrelated product's quantity together into a meaningless number.
    if (!filters.productCode) {
      setRows([]);
      setLoading(false);
      return;
    }
    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
      ...(groupBy ? { groupBy } : {}),
    };
    setLoading(true);
    getBriefReport(params).then((res) => {
      setRows(res);
      setLoading(false);
    });
  }, [filters, groupBy]);

  function set(key, value) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function setYear(year) {
    setFilters((f) => ({ ...f, ...(year ? yearDateRange(year) : { from: '', to: '' }) }));
  }

  function setChannel(value) {
    const availableSubChannels = value ? options.channelSubChannels?.[value] ?? [] : null;
    const subChannelStillValid = !availableSubChannels || availableSubChannels.includes(filters.subChannel);
    setFilters((f) => ({ ...f, channel: value, subChannel: subChannelStillValid ? f.subChannel : '' }));
  }

  const channelItems = (options.channels || []).map((c) => ({ code: c, name: c }));
  const availableSubChannels = filters.channel
    ? options.channelSubChannels?.[filters.channel] ?? []
    : options.subChannels || [];
  const subChannelItems = availableSubChannels.map((s) => ({ code: s, name: s }));

  const groupByLabel = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label;

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">Brief Report</h1>
      <p className="text-sm text-gray-600 mb-4">
        A summary view, not a row-level detail table. Pick a Product (and optionally Salesman, Customer,
        Channel, Sub-Channel, or a date range) to see its total quantity — then choose a "Group by"
        dimension to split that same total into a breakdown instead, e.g. quantity per Channel, or
        quantity per Month.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => set('from', e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => set('to', e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <YearSelect years={options.years} value={detectSelectedYear(filters)} onChange={setYear} />
        <SearchableSelect label="Product" value={filters.productCode} onChange={(v) => set('productCode', v)} items={options.products} />
        <SearchableSelect label="Salesman" value={filters.salesmanCode} onChange={(v) => set('salesmanCode', v)} items={options.salesmen} />
        <SearchableSelect label="Customer" value={filters.customerCode} onChange={(v) => set('customerCode', v)} items={options.customers} />
        <SearchableSelect label="Channel" value={filters.channel} onChange={setChannel} items={channelItems} />
        <SearchableSelect label="Sub-Channel" value={filters.subChannel} onChange={(v) => set('subChannel', v)} items={subChannelItems} />

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Group by</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            {GROUP_BY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <button onClick={() => setFilters(getDefaultDashboardFilters())} className="text-xs text-blue-600 hover:underline mb-1.5">
          Clear filters
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-700">
            {groupBy ? `Quantity by ${groupByLabel}` : 'Total Quantity'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              {filters.productCode && <th className="px-4 py-2 font-medium">Product</th>}
              <th className="px-4 py-2 font-medium">{groupBy ? groupByLabel : ''}</th>
              <th className="px-4 py-2 font-medium">Unit</th>
              <th className="px-4 py-2 font-medium text-right">Qty</th>
              <th className="px-4 py-2 font-medium text-right">Invoices</th>
              {canViewGP && (
                <>
                  <th className="px-4 py-2 font-medium text-right">Revenue</th>
                  <th className="px-4 py-2 font-medium text-right">Cost</th>
                  <th className="px-4 py-2 font-medium text-right">GP</th>
                  <th className="px-4 py-2 font-medium text-right">GP %</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={(canViewGP ? 8 : 4) + (filters.productCode ? 1 : 0)} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : !filters.productCode ? (
              <tr>
                <td colSpan={canViewGP ? 8 : 4} className="px-4 py-6 text-center text-gray-400">
                  Select a Product above to see its total quantity.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={(canViewGP ? 8 : 4) + (filters.productCode ? 1 : 0)} className="px-4 py-6 text-center text-gray-400">
                  No data for the selected filters
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.key} className="border-t border-gray-100">
                  {filters.productCode && <td className="px-4 py-2">{r.productName}</td>}
                  <td className="px-4 py-2 font-medium">{r.label}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.unit || '(Not Set)'}</td>
                  <td className="px-4 py-2 text-right">{Number(r.qty).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-2 text-right">{r.count.toLocaleString()}</td>
                  {canViewGP && (
                    <>
                      <td className="px-4 py-2 text-right">{formatCurrency(r.revenue)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(r.cost)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{formatCurrency(r.gpAmount)}</td>
                      <td className="px-4 py-2 text-right">{formatPercent(r.gpPercent)}</td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
